import { getNavigationLinks } from "@/services/api/navbar";
import { FilterField, ProductListingFilterProps } from "@/components/filter-panel/types";
import { FILTER_CONFIG, FilterConfigKey } from "@/constants/filter-config";
import { useQuery } from "@tanstack/react-query";
import { useParams, useRouter } from "next/navigation";
import { useCallback, useMemo } from "react";
export interface NavigationItem {
  id?: string | number;
  title?: string;
  heading?: string;
  name?: string;
  url?: string;
  is_navbar_label?: boolean;
  product_count?: number;
  children?: NavigationItem[];
  subTypes?: NavigationItem[];
  [key: string]: unknown;
}
export function useProductListingFilter({
  isCollection,
  isThemeCake = false,
  initialNavigationData,
  searchParams: searchParamsProp
}: ProductListingFilterProps) {
  const params = useParams();
  const currentSlug = params.slug as string;
  const router = useRouter();

  // Convert searchParams prop to URLSearchParams
  const searchParams = useMemo(() => {
    const p = new URLSearchParams();
    if (searchParamsProp) {
      Object.entries(searchParamsProp).forEach(([key, value]) => {
        if (value !== undefined) {
          const stringValue = Array.isArray(value) ? value[0] : value;
          if (stringValue !== undefined) {
            p.set(key, stringValue);
          }
        }
      });
    }
    return p;
  }, [searchParamsProp]);
  const {
    data: navigation,
    isLoading: isNavigationLoading
  } = useQuery({
    queryKey: ["navigation"],
    queryFn: () => getNavigationLinks(),
    staleTime: Infinity,
    initialData: initialNavigationData as NavigationItem[] | undefined,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false
  });
  const finalNavigation = useMemo(() => {
    return navigation && navigation.length > 0 ? navigation as NavigationItem[] : initialNavigationData as NavigationItem[] || [];
  }, [navigation, initialNavigationData]);
  const isLoadingState = isNavigationLoading && finalNavigation.length === 0;
  const currentCategory = useMemo(() => {
    if (!finalNavigation || finalNavigation.length === 0) return null;
    const slugLower = currentSlug ? String(currentSlug).toLowerCase() : "";
    const categoryIdParam = searchParams.get("category_id");
    const parentIdParam = searchParams.get("parent_id");
    const idParam = searchParams.get("id");
    const isSlugMatch = (url: string) => {
      if (!url || !slugLower) return false;
      const parts = url.split("?");
      const cleanUrl = (parts[0] || "").toLowerCase();
      const targetPath = `/listing/${slugLower}`;
      return cleanUrl === targetPath || cleanUrl.endsWith(targetPath);
    };
    const hasIdMatch = (item: NavigationItem, id: string) => {
      if (!item) return false;
      const itemId = String(item.id);
      const cleanId = id.replace("label-", "");
      if (itemId === id || itemId === `label-${id}` || itemId === cleanId) return true;
      if (item.url) {
        try {
          const urlObj = new URL(item.url, "http://localhost");
          if (urlObj.searchParams.get("category_id") === id || urlObj.searchParams.get("id") === id) {
            return true;
          }
        } catch (_e) {}
      }
      return false;
    };
    const matchesItem = (item_0: NavigationItem): boolean => {
      if (item_0.url && isSlugMatch(item_0.url)) return true;
      if (categoryIdParam && hasIdMatch(item_0, categoryIdParam)) return true;
      if (parentIdParam && hasIdMatch(item_0, parentIdParam)) return true;
      if (item_0.subTypes && item_0.subTypes.length > 0) {
        if (item_0.subTypes.some((sub: NavigationItem) => matchesItem(sub))) return true;
      }
      if (item_0.children && item_0.children.length > 0) {
        if (item_0.children.some((child: NavigationItem) => matchesItem(child))) return true;
      }
      return false;
    };
    let matched = finalNavigation.find((item_1: NavigationItem) => matchesItem(item_1));
    if (matched) return matched;
    if (idParam) {
      matched = finalNavigation.find((item_2: NavigationItem) => {
        const checkIdParam = (sub_0: NavigationItem): boolean => {
          if (hasIdMatch(sub_0, idParam)) return true;
          if (sub_0.subTypes && sub_0.subTypes.length > 0) {
            return sub_0.subTypes.some(checkIdParam);
          }
          if (sub_0.children && sub_0.children.length > 0) {
            return sub_0.children.some(checkIdParam);
          }
          return false;
        };
        return checkIdParam(item_2);
      });
      if (matched) return matched;
    }
    matched = finalNavigation.find((item_3: NavigationItem) => {
      if (!item_3.url) return false;
      return item_3.url.toLowerCase().includes(slugLower);
    });
    if (matched) return matched;
    return finalNavigation.find((item_4: NavigationItem) => {
      if (!item_4.url) return false;
      const itemBasePath = item_4.url.split("?")[0];
      return itemBasePath === `/listing/${currentSlug}`;
    });
  }, [finalNavigation, currentSlug, searchParams]);
  const generateFilterSchema = useMemo(() => {
    const schema: FilterField[] = [];
    if (!isCollection) {
      schema.push({
        ...FILTER_CONFIG.price
      } as FilterField);
    }
    if (!currentCategory) return schema;
    const getValueByPath = (obj: Record<string, unknown>, path: string): unknown => {
      return path.split(".").reduce<unknown>((acc, part) => {
        if (acc && typeof acc === "object" && part in acc) {
          return (acc as Record<string, unknown>)[part];
        }
        return undefined;
      }, obj);
    };
    const mapItemsToOptions = (itemsList: NavigationItem[]): FilterField["options"] => {
      return (itemsList || []).map((item_5: NavigationItem) => ({
        id: item_5.id ? item_5.id.toString() : "",
        label: item_5.heading || item_5.title || "",
        count: item_5.product_count ?? 0,
        url: item_5.url || "",
        children: item_5.children || item_5.subTypes ? mapItemsToOptions((item_5.children || item_5.subTypes) as NavigationItem[]) : undefined
      }));
    };
    const categoryConfigs: FilterConfigKey[] = ["categories"];
    categoryConfigs.forEach(key_0 => {
      const config = FILTER_CONFIG[key_0];
      const items = getValueByPath(currentCategory as unknown as Record<string, unknown>, config.dataKey) as NavigationItem[] | undefined;
      if (items && items.length > 0) {
        schema.push({
          type: config.type,
          label: config.label,
          id: config.id,
          options: mapItemsToOptions(items)
        });
      }
    });
    return schema;
  }, [currentCategory, isCollection]);
  const isNavbarLabel = useMemo(() => {
    return Boolean(currentCategory?.is_navbar_label || currentCategory?.id && String(currentCategory.id).startsWith("label-"));
  }, [currentCategory]);
  const onClearFilters = useCallback(() => {
    const currentParams = new URLSearchParams(window.location.search);
    const paramsToKeep = new URLSearchParams();
    const cleanId_0 = currentCategory?.id ? String(currentCategory.id).replace("label-", "") : null;
    if (isNavbarLabel && cleanId_0) {
      paramsToKeep.set("category_id", cleanId_0);
    } else if (cleanId_0) {
      paramsToKeep.set("id", cleanId_0);
    } else {
      if (currentParams.has("id")) {
        paramsToKeep.set("id", currentParams.get("id")!);
      }
      if (currentParams.has("category_id")) {
        paramsToKeep.set("category_id", currentParams.get("category_id")!);
      }
    }
    if (currentParams.has("parent_id")) {
      paramsToKeep.set("parent_id", currentParams.get("parent_id")!);
    }
    const newSearch = paramsToKeep.toString();
    let basePathname = window.location.pathname;
    if (currentCategory && currentCategory.url) {
      try {
        const urlObj_0 = new URL(currentCategory.url, "http://localhost");
        basePathname = urlObj_0.pathname;
      } catch (_e_0) {
        basePathname = currentCategory.url.split("?")[0] || window.location.pathname;
      }
    }
    const newUrl = `${basePathname}${newSearch ? `?${newSearch}` : ""}`;
    window.dispatchEvent(new Event("route-change-start"));
    if (isThemeCake && !newSearch) {
      router.push(basePathname, {
        scroll: false
      });
    } else {
      router.push(newUrl, {
        scroll: false
      });
    }
  }, [router, isThemeCake, currentCategory, isNavbarLabel]);
  return {
    finalNavigation,
    currentCategory,
    filterSchema: generateFilterSchema,
    isNavbarLabel,
    onClearFilters,
    isLoadingState,
    searchParams
  };
}